import { DateTime } from '@ksyos/date-time';
import type { PatientUnavailableServices } from '@common/interfaces/patientUnavailableServices';
import type { IPatientsListFilters } from './../Patients';
import type { IPatientInOrganization } from './PatientSearch';
import type { IPatientSearchResult } from './PatientSearchStartBar';
import { fetchJson } from '../../../../store/fetch';
import { emptyPatient } from './emptyPatient';

export interface IPatientSearchInformation {
    birthDate: string | null;
    identifierSocialSecurity: string | null;
    name?: string | null;
}

export interface IVecozoInsurance {
    endDate: string | null;
    insurancePolicyNumber: any;
    startDate: string;
    uzovi: {
        code: any;
        name: any;
    };
}

export async function findPatientInKsyos(patientSearchInformation: IPatientSearchInformation) {
    const url = '/api/patients/notInvolvedWithPractitionerOrOrganization';
    const result = await fetchJson(url, 'POST', patientSearchInformation);
    const patient = result.json.items[0] as IPatientSearchResult;

    if (!patient) {
        return;
    }

    return { patient };
}

export async function findPatientInSbvzAndVecozo(
    patientSearchInformation: IPatientSearchInformation
): Promise<{ insurance?: IVecozoInsurance; patient: IPatientSearchResult } | undefined> {
    const patientFromSbvz = await findPatientInSbvz(patientSearchInformation);
    const patientFromVecozo = await findPatientInVecozo(patientSearchInformation);
    const insurance = patientFromVecozo?.insurance;
    const identifierInsurance = insurance?.insurancePolicyNumber;

    if (patientFromSbvz) {
        return {
            patient: {
                ...emptyPatient(),
                ...patientFromSbvz,
                identifierInsurance,
            },
            insurance,
        };
    } else if (patientFromVecozo?.patient) {
        return {
            patient: {
                ...emptyPatient(),
                ...patientFromVecozo.patient,
                identifierInsurance,
            },
            insurance,
        };
    }
}

export async function findPatientInSbvz(patientSearchInformation: IPatientSearchInformation) {
    const sbvzResult = await fetchJson('/api/patients/updatePersonalData', 'POST', {
        bsn: patientSearchInformation.identifierSocialSecurity,
        birthDate: patientSearchInformation.birthDate,
    });

    return sbvzResult.json.patient ? (sbvzResult.json.patient as IPatientSearchResult) : undefined;
}

export async function findPatientInVecozo(patientSearchInformation: IPatientSearchInformation) {
    const vecozoResult = await fetchJson('/api/insuranceHistories/fromSource', 'POST', {
        birthDate: patientSearchInformation.birthDate,
        identifierSocialSecurity: patientSearchInformation.identifierSocialSecurity,
        identifierInsurance: null,
        zip: null,
        houseNumber: null,
        houseLetter: null,
        houseNumberSuffix: null,
    });

    if (vecozoResult.json.sourceResult) {
        const insurance = vecozoResult.json.sourceResult.insurance
            ? {
                  insurancePolicyNumber: vecozoResult.json.sourceResult.insurance.identifierInsurance,
                  startDate: DateTime.from(vecozoResult.json.sourceResult.insurance.startdate).format('YYYY-MM-DD'),
                  endDate: vecozoResult.json.sourceResult.insurance.enddate
                      ? DateTime.from(vecozoResult.json.sourceResult.insurance.enddate).format('YYYY-MM-DD')
                      : null,
                  uzovi: {
                      name: vecozoResult.json.sourceResult.insurance.label,
                      code: vecozoResult.json.sourceResult.insurance.uzoviCode,
                  },
              }
            : undefined;

        const patient = vecozoResult.json.sourceResult.nameFamily
            ? {
                  nameGiven: vecozoResult.json.sourceResult.nameGiven || null,
                  nameInfix: vecozoResult.json.sourceResult.nameInfix || null,
                  nameFamily: vecozoResult.json.sourceResult.nameFamily || null,
                  nameFamily2: vecozoResult.json.sourceResult.nameFamily2 || null,
                  nameInfix2: vecozoResult.json.sourceResult.nameInfix2 || null,
                  gender: vecozoResult.json.sourceResult.gender || null,
                  isDeceased: vecozoResult.json.sourceResult.isDeceased || false,
                  identifierSocialSecurity: vecozoResult.json.sourceResult.identifierSocialSecurity || null,
                  birthDate: vecozoResult.json.sourceResult.birthDate || null,
              }
            : undefined;

        return { insurance, patient };
    }
}

export async function findPatientsInOrganization(
    patientSearchInformation?: IPatientSearchInformation,
    patientSearchFilters?: IPatientsListFilters,
    page?: number
) {
    const urlParameters = new URLSearchParams();
    if (page) {
        urlParameters.append('page', String(page));
    }
    if (patientSearchFilters) {
        for (const [key, value] of Object.entries(patientSearchFilters)) {
            urlParameters.append(key, value);
        }
    }

    const result = await fetchJson(
        `/api/patients/involvedWithPractitionerOrOrganization?${urlParameters.toString()}`,
        'POST',
        patientSearchInformation
    );
    const patients = result.json.items as IPatientInOrganization[];
    const hasMoreItems = Boolean(result.json.hasMoreItems);

    return { hasMoreItems, patients };
}

export async function findUnavailableServices(patientIds: string[]) {
    if (patientIds.length > 0) {
        return (await fetchJson('/api/patients/unavailableServices', 'POST', { patientIds })).json
            .unavailableServices as PatientUnavailableServices;
    } else {
        return {};
    }
}
